import { ref } from 'vue';
import UserService from '../../../services/user.service';
import { exportToExcel } from './exportExcel';
import store from '../../../store/index.js';
import { useToast } from 'vue-toastification';
import router from '../../../router.js';

// Helper function to show alert message directly to user
// for renewable energy upload hasInvalidRecords and hasChild task type
function showAlertMessage(responseAlert, response_alert) {
  if (!responseAlert.visible) {
    return;
  }

  let alertMessage = `${responseAlert.title}\n\n`;
  
  // Add success message if there are successful records
  if (response_alert.success_count > 0) {
    alertMessage += `✓ ${responseAlert.success_desc}\n`;
  }
  
  // Add pending/error message if there are pending records
  if (response_alert.pending_count > 0) {
    alertMessage += `⚠ ${responseAlert.pending_desc}\n`;
  }
  
  // Add duplicate message if there are duplicate records
  if (response_alert.duplicate_count > 0) {
    alertMessage += `⚠ ${responseAlert.duplicate_desc}\n`;
  }
  
  // Determine toast type and icon based on responseAlert.type
  const toastType = responseAlert.type === 'success' ? 'success' : 
                   responseAlert.type === 'warning' ? 'warning' : 
                   responseAlert.type === 'error' ? 'error' : 'info';
  
  const toastIcon = responseAlert.type === 'success' ? 'fa fa-check' :
                   responseAlert.type === 'warning' ? 'fa fa-exclamation-triangle' :
                   responseAlert.type === 'error' ? 'fa fa-times' : 'fa fa-info';
  
  // Show the alert using toast notification
  useToast()[toastType](alertMessage.trim(), {
    icon: toastIcon,
    position: "bottom-right",
    timeout: 8000, // Longer timeout to allow reading the message
  });
}

// helper function to show session upload summary
function showSessionUploadSummary(resultData) {
  // Process the result data array to generate summary message
  if (Array.isArray(resultData)) {
    let total_count = 0;
    let success_count = 0;
    let success_sum_kwh = 0;
    let pending_count = 0;
    let pending_sum_kwh = 0;
    let invalid_count = 0;
    let invalid_sum_kwh = 0;
    let duplicate_count = 0;

    // Aggregate data from all result objects
    for (let data of resultData) {
      total_count += data["total_count"] || 0;
      success_count += data["success_count"] || 0;
      success_sum_kwh += data["success_sum_kwh"] || 0;
      pending_count += data["pending_count"] || 0;
      pending_sum_kwh += data["pending_sum_kwh"] || 0;
      invalid_count += data["invalid_count"] || 0;
      invalid_sum_kwh += data["invalid_sum_kwh"] || 0;
      duplicate_count += data["duplicate_count"] || 0;
    }

    // Generate message body similar to Python method
    let body = `Session Uoload Summary:\nFrom the ${total_count} records provided:`;
    if (success_count > 0) {
      body += `\n\nSuccessful:`;
      body += `\n${success_count} records totalling ${Math.round(success_sum_kwh * 100) / 100} kWh were uploaded successfully.`;
    }
    if (pending_count > 0) {
      body += `\n\nPending:`;
      body += `\n${pending_count} records totalling ${Math.round(pending_sum_kwh * 100) / 100} kWh appear to have errors and are currently pending.`;
    }
    if (invalid_count > 0) {
      body += `\n\nInvalid Data Entry:`;
      body += `\n${invalid_count} records totalling ${Math.round(invalid_sum_kwh * 100) / 100} kWh appear to have errors and are currently pending.`;
    }
    if (duplicate_count > 0) {
      body += `\n\nDuplicate:`;
      body += `\n${duplicate_count} records were already in the database, and have not been uploaded again.`;
    }
    // Show success message with the generated body
    useToast().success(body, {
      icon: "fa fa-check",
      position: "bottom-right",
      timeout: 10000, // Longer timeout to allow reading the detailed message
    });
  }
}

// actions
// call action in store/modules/tasksProcessing.js to start loading state
const startLoading = (itemKey, taskId, taskType, status = 'PENDING') => {
  store.dispatch('tasksProcessing/startLoading', { 
    itemKey, 
    taskId, 
    taskType, 
    status
  });
};

const clearLoadingState = (itemKey) => {
  store.dispatch('tasksProcessing/clearLoadingState', itemKey);
};

/**
 * Update loading state by checking all tasks in session storage
 * Clears loading state for tasks with SUCCESS, ERROR, or REVOKED status
 */
const updateLoading = (result) => {
  try {
    const taskIdsString = sessionStorage.getItem('taskIds');
    if (taskIdsString) {
      const taskDetails = JSON.parse(taskIdsString);

      // Check each task in the array
      taskDetails.forEach(task => {
        if (task.taskStatus === 'SUCCESS' || 
            task.taskStatus === 'ERROR' || 
            task.taskStatus === 'REVOKED' ||
            task.taskStatus === 'FAILURE') {
            // Clear loading state for completed tasks
            clearLoadingState(task.taskName);
            console.log(`Cleared loading state for task: ${task.taskName} (${task.taskId}) - Status: ${task.taskStatus}`);
            if (task.taskStatus === 'ERROR' || task.taskStatus === 'REVOKED' || task.taskStatus === 'FAILURE') {
              useToast().error(`Task ${task.taskName} ended with status: ${task.taskStatus}`, {
                icon: "fa fa-times",
                position: "bottom-right",
                timeout: 5000,
              });
              if (result) {
                useToast().error(`${result}`, {
                  icon: "fa fa-times",
                  position: "bottom-right",
                  timeout: 5000,
                });
              }
            }
            // if (task.taskStatus === 'SUCCESS' && task.taskType === 'hasChild') {
            //   useToast().success(`Task ${task.taskName} ended with status: ${task.taskStatus}`, {
            //     icon: "fa fa-check",
            //     position: "bottom-right",
            //     timeout: 5000,
            //   });
            // }
        }
        if (task.taskStatus === 'STARTED' || task.taskStatus === 'PENDING') {
          startLoading(task.taskName, task.taskId, task.taskType, task.taskStatus);
        }
      });
    } else {
      console.log('No tasks found in session storage');
    }
  } catch (error) {
    console.error('Error updating loading state:', error);
  }
};

// add a task to session storage with taskName, taskId, taskStatus
export function addTaskIdtoSessionStorage(taskName, taskId, taskStatus, taskType, taskParams) {
  const taskIdsString = sessionStorage.getItem('taskIds');
  let taskIds = [];
  
  if (taskIdsString) {
    try {
      taskIds = JSON.parse(taskIdsString);
    } catch (error) {
      console.error('Error parsing taskIds from sessionStorage:', error);
      taskIds = [];
    }
  }
  
  // Add new task to the array
  /*
  * taskParams is an optional parameter, if not provided, it will be null
  * taskParams:
    1. taskType: exportToExcel
       taskParams:
      {
        skipKeys,
        fileName,
        newWorksheetName,
        title,
      }

    2. taskType: message
      taskParams:
      {
        success_message,
        error_message,
      }

    3. taskType: hasInvalidRecords
      taskParams:
      {
          response_alert: {
          all_rows_count: 0,
          success_count: 0,
          success_sum_kwh: 0,
          pending_count: 0,
          pending_sum_kwh: 0,
          error_count: 0,
          error_sum_kwh: 0,
          duplicate_count: 0,
        },

          responseAlert: {
          visible: false,
          title: "",
          type: "info",
          success_desc: "",
          pending_desc: "",
          duplicate_desc: "",
        },
      }
    
      5. taskType: hasChild
      taskParams: {}
  */
  taskIds.push({
    taskName: taskName,
    taskId: taskId,
    taskStatus: taskStatus,
    taskResult: null,
    taskType: taskType,
    taskParams: taskParams,
    taskChildren: null,
  });
  
  // Save back to sessionStorage as JSON string
  sessionStorage.setItem('taskIds', JSON.stringify(taskIds));

  // start loading
  startLoading(taskName, taskId, taskType, taskStatus);
}

// Update task status in session storage with taskId and taskStatus
export function updateTaskStatusInSessionStorage(taskId, taskStatus, taskResult, taskChildren) {
  // console.log("receive updating task status in session storage:", taskId, taskStatus, taskResult, taskChildren);
  
  const taskIdsString = sessionStorage.getItem('taskIds');
  
  if (taskIdsString) {
    try {
      const taskIds = JSON.parse(taskIdsString);
      
      // Find the task and update its status
      const taskIndex = taskIds.findIndex(task => task.taskId === taskId);
      
      if (taskIndex !== -1) {
        taskIds[taskIndex].taskStatus = taskStatus;
        taskIds[taskIndex].taskResult = taskResult;
        taskIds[taskIndex].taskChildren = taskChildren;
        sessionStorage.setItem('taskIds', JSON.stringify(taskIds));
        // console.log(`Updated task ${taskId} status :${taskStatus} with result : ${taskResult} with children : ${taskChildren}` );
      } else {
        console.warn(`Task with ID ${taskId} not found in sessionStorage`);
      }

    } catch (error) {
      console.error('Error updating task status in sessionStorage:', error);
    }
  } else {
    console.warn('No taskIds found in sessionStorage');
  }
}

// get task result if has task from session storage with status SUCCESS and result hasResult
export async function getTaskResultFromSessionStorage(taskId) {
  const taskIdsString = sessionStorage.getItem('taskIds');
  if (taskIdsString) {
    const taskIds = JSON.parse(taskIdsString);
    const task = taskIds.find(task => task.taskId === taskId && task.taskStatus === 'SUCCESS');
    const taskType = task.taskType || "noAction";
    const taskParams = task.taskParams || {};
    // console.log("Find ongoing task:", task.taskName, task.taskId, taskType);
    const response = await UserService.checkCeleryTaskStatus(task.taskId);

    try {

  
    if (response.data && response.data.status === 'SUCCESS') {
      const resultData = response.data.result;

      let returnResult = null;

      // if task type is message, no further action is needed, show success message
      if (taskType == "message") {
        console.log("Task completed, task type is message");
        useToast().success(taskParams.success_message, {
          icon: "fa fa-check",
          position: "bottom-right",
          timeout: 3000,
        });
        returnResult = "SUCCESS";
      }

      // if task type is hasChild, means this task has sub tasks, keep tracking the sub tasks
      else if (taskType == "hasChild") {
        if (task.taskResult == "hasResult") {
          // console.log(`${task.taskName} - child task ${taskId} has result`);
          // Call the helper function to show session upload summary
          showSessionUploadSummary(resultData);
          returnResult = "SUCCESS";
        }
        console.log(`Task ${task.taskName} has child tasks, keep tracking the sub tasks ${task.taskChildren}`);
        if (task.taskChildren) {
          task.taskChildren.forEach(child => {
            addTaskIdtoSessionStorage('session_upload', child.child_id, child.child_status, "hasChild", {});
            createCeleryTaskPoller(child.child_id, 30000).startPolling();
          });
        }
      }

      // renewable energy upload, return message with invalid records check
      else if (taskType == "hasInvalidRecords") {
        console.log("Task completed, task type is hasInvalidRecords");
        let message = "Renewable energy upload - File processed successfully!";

        // Build detailed message with results
        if (resultData.valid_count !== undefined || resultData.invalid_count !== undefined) {
          const parts = ["Renewable energy upload - File processed successfully!"];
          
          if (resultData.valid_count !== undefined) {
            parts.push(`✓ Valid records: ${resultData.valid_count}`);
          }
          
          if (resultData.invalid_count !== undefined) {
            parts.push(`✗ Invalid records: ${resultData.invalid_count}`);
            
            // Add details about invalid records if available
            if (resultData.invalid_records && Array.isArray(resultData.invalid_records) && resultData.invalid_records.length > 0) {
              // Show first few error examples
              const maxErrors = 3;
              const errorCount = resultData.invalid_records.length;
              
              if (errorCount <= maxErrors) {
                resultData.invalid_records.forEach((record) => {
                  if (record.error || record.reason) {
                    parts.push(`  • ${record.error || record.reason}`);
                  }
                });
              } else {
                resultData.invalid_records.slice(0, maxErrors).forEach((record) => {
                  if (record.error || record.reason) {
                    parts.push(`  • ${record.error || record.reason}`);
                  }
                });
                parts.push(`  • ... and ${errorCount - maxErrors} more errors`);
              }
            }
          }
          
          message = parts.join('\n');
        }

        // Use appropriate toast type based on results
        const hasInvalidRecords = resultData.invalid_count > 0;
        const toastMethod = hasInvalidRecords ? useToast().warning : useToast().success;
        
        toastMethod(message, {
          icon: hasInvalidRecords ? "fa fa-exclamation-triangle" : "fa fa-check",
          position: "bottom-right",
          timeout: hasInvalidRecords ? 8000 : 5000, // Longer timeout if there are errors to read
        });
        
        // Update the response alert if needed
        if (resultData.valid_count !== undefined || resultData.invalid_count !== undefined) {
          taskParams.response_alert.success_count = resultData.valid_count || 0;
          taskParams.response_alert.error_count = resultData.invalid_count || 0;
          
          taskParams.responseAlert.visible = true;
          taskParams.responseAlert.type = hasInvalidRecords ? "warning" : "success";
          taskParams.responseAlert.title = `Renewable energy upload processing complete`;
          taskParams.responseAlert.success_desc = resultData.valid_count > 0 
            ? `${resultData.valid_count} records processed successfully.` 
            : "";
          taskParams.responseAlert.pending_desc = resultData.invalid_count > 0 
            ? `${resultData.invalid_count} records had errors and were not processed.` 
            : "";
        }

        // Show alert message directly to user
        showAlertMessage(taskParams.responseAlert, taskParams.response_alert);
        returnResult = "SUCCESS";

      }

      // if task is export excel, it will download result data and export to excel
      else if (task.taskResult == "hasResult" && taskType === 'exportToExcel') {
        console.log("Result data downloaded successfully");
        console.log("Exporting Excel file...");
        await exportToExcel({
          dataList: resultData.session_logs,
          skipKeys: taskParams.skipKeys,
          fileName: taskParams.fileName,
          newWorksheetName: taskParams.newWorksheetName,
          title: taskParams.title,
        });  
      }

      // if task is uk emission intensity update, it will show the result data
      else if (task.taskResult == "hasResult" && taskType === 'updateUKIntensity') {
        console.log("Task completed, task type is uk emission intensity update");
        console.log(resultData);
        let message = `UK emission intensity update completed! \n
         ${resultData.total_records} records were processed, \n 
         ${resultData.duplicate_records} records were duplicate, \n 
         ${resultData.success_inserted_records} records were successfully inserted. \n
         ${resultData.fill_missing_result.message} : \n 
         Missing intervals: \n` + resultData.fill_missing_result.missing_intervals.join(', ') + `\n \n`;
        useToast().success(message, {
          icon: "fa fa-check",
          position: "bottom-right",
          timeout: 5000,
        });
        returnResult = "SUCCESS";
      }

      else if (task.taskResult == "hasResult" && taskType === 'reports') {
        let message = `Task completed, ${task.taskName} report result is ready`;
        useToast().success(message, {
          icon: "fa fa-check",
          position: "bottom-right",
          timeout: 5000,
        });
        returnResult = resultData;
      }

      else {
        console.log("Task type is not supported");
        useToast().success(`Task ${task.taskName} ended with status: ${task.taskStatus}, no further action is needed`, {
          icon: "fa fa-check",
          position: "bottom-right",
          timeout: 5000,
        });
      }

      return returnResult;
    }
    else {
      if (taskType == "message") {
        console.log("Task completed, task type is message");
        useToast().error(taskParams.error_message + task.taskResult, {
          icon: "fa fa-times",
          position: "bottom-right",
          timeout: 5000,
        });
      }
      else if (taskType == "exportToExcel") {
        console.log("Task completed, task type is exportToExcel");
        useToast().error("Task failed, cannot export Excel file: " + task.taskResult, {
          icon: "fa fa-times",
          position: "bottom-right",
          timeout: 5000,
        });
      }
      console.log("Result data not found");
      return null;
    }
    } catch (e) {
      console.error('Error getting task result from sessionStorage:', e);
    } finally {
        // after task type done, remove task from session storage
        removeTaskFromSessionStorage(task.taskId);
        // clear loading state
        clearLoadingState(task.taskName);

        updateLoading();
    }
  }
  return null;
}

// remove task from session storage with taskId
// use to clear completed (failed, revoked, success)  tasks from session storage
export function removeTaskFromSessionStorage(taskId) {
  const taskIdsString = sessionStorage.getItem('taskIds');
  if (taskIdsString) {
    const taskIds = JSON.parse(taskIdsString);
    const task = taskIds.find(task => task.taskId === taskId);
    taskIds.splice(taskIds.indexOf(task), 1);
    sessionStorage.setItem('taskIds', JSON.stringify(taskIds));
  }
  else {
    console.warn('No taskIds found in sessionStorage');
  }
}



/**
 * Creates a reusable Celery task polling utility
 * @param {string} taskId - The Celery task ID to poll
 * @param {number} pollingIntervalInMs - Polling interval in milliseconds (default: 15000)
 * @returns {Object} Polling control object with methods and reactive state
 */
export function createCeleryTaskPoller(taskId, pollingIntervalInMs = 15000) {
  // Reactive state
  const taskStatus = ref(null);
  const isLoading = ref(false);
  const error = ref(null);
  const result = ref(null);
  
  // Internal polling state
  let pollingInterval = null;
  let isPolling = ref(false);
  
  /**
   * Start polling the task status
   */
  const startPolling = () => {
    if (isPolling.value) {
      console.warn('Polling is already active');
      return;
    }
    
    isPolling.value = true;
    isLoading.value = true;
    error.value = null;
    
    // Check immediately
    checkTaskStatus();
    
    // Set up interval
    pollingInterval = setInterval(() => {
      checkTaskStatus();
    }, pollingIntervalInMs);
    
    console.log(`Started polling task ${taskId} every ${pollingIntervalInMs}ms`);
  };
  
  /**
   * Stop polling the task status
   */
  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
    
    isPolling.value = false;
    isLoading.value = false;
    
    console.log(`Stopped polling task ${taskId}`);
  };
  
  /**
   * Check the current task status
   */
  const checkTaskStatus = async () => {
    if (!taskId) {
      error.value = 'No task ID provided';
      return;
    }
    
    try {
      // const response = await UserService.checkCeleryTaskStatus(taskId);
      const response = await UserService.checkMultipleCeleryTaskStatus([taskId]);
      // console.log("Task status response:");
      
      // Always call updateTaskStatusInSessionStorage when we get any response
      if (response && response.data) {
        // Extract status from response - handle the array structure from checkMultipleCeleryTaskStatus
        let currentStatus = null;
        let taskResult = null;
        let taskChildren = null;
        
        // Response is an array of task objects
        if (Array.isArray(response.data) && response.data.length > 0) {
          // Find our task in the array
          const taskData = response.data.find(task => task.task_id === taskId);
          if (taskData) {

            
            currentStatus = taskData.status;
            taskResult = taskData.result;
            taskChildren = taskData.children;
          }
        } else if (response.data.status) {

          // Fallback for single task response (if needed)
          currentStatus = response.data.status;
          taskResult = response.data.result;
          taskChildren = response.data.children;
        }
        
        // Update session storage with current status if we have one
        if (currentStatus) {
          // console.log("Updating task status in session storage:", taskId, currentStatus, taskResult, taskChildren);
          updateTaskStatusInSessionStorage(taskId, currentStatus, taskResult, taskChildren);

          taskStatus.value = currentStatus;
          result.value = taskResult;

          // Handle different status types
          switch (currentStatus.toUpperCase()) {
            case 'SUCCESS':
              handleTaskSuccess({ result: taskResult });
              break;
            case 'FAILURE':
              // handleTaskFailure();
              handleTaskFailure(taskId, taskResult);
              break;
            case 'REVOKED':
              handleTaskRevoked(taskId);
              break;
            case 'PENDING':
            case 'STARTED':
            case 'RETRY':
              // Continue polling
              console.log(`Task ${taskId} is ${currentStatus}`);
              break;
            default:
              console.log(`Unknown task status: ${currentStatus}`);
          }


        } else {
          console.warn("No status found in response for task:", taskId);
        }
      }
    } catch (err) {
      console.error("Error checking task status:", err);
      error.value = err.message || 'Failed to check task status';
      
      // Update session storage with error status
      console.log("Updating task status in session storage with error:", taskId, 'ERROR');
      updateTaskStatusInSessionStorage(taskId, 'ERROR');
      
      if (err.response && err.response.status === 404) {
        // Task not found, stop polling
        stopPolling();
        error.value = 'Task not found. Please try again.';
        // Update session storage with not found status
        updateTaskStatusInSessionStorage(taskId, 'NOT_FOUND');
      }

      if (err.response && err.response.status === 500) {
        // Task not found, stop polling
        stopPolling();
        error.value = 'Task failed. Please try again.';
        // Update session storage with not found status
        updateTaskStatusInSessionStorage(taskId, 'ERROR');
      }

       if (err.response && (err.response.status === 401 || err.response.status === 403)) {
         // Authentication error, logout user and redirect to login
         stopPolling();
         error.value = 'Session expired. Please login again.';
               
         // Update session storage with not found status
         updateTaskStatusInSessionStorage(taskId, 'NOT_FOUND');
         
         // Redirect to login
         router.push('/login');
       }
    }
  };
  
  /**
   * Handle successful task completion
   * stop polling
   * check if further action is needed: if has result, get task result from session storage
   */
  const handleTaskSuccess = (data) => {
    stopPolling();
    result.value = data.result || null;
    console.log(`Task ${taskId} completed successfully`);
    getTaskResultFromSessionStorage(taskId).then(storageResult => {
      if (typeof(storageResult) === 'object') {
        result.value = storageResult;
      }
    });
  };
  
  /**
   * Handle task failure
   * stop polling
   * stop loading
   * remove task from session storage
   */
  const handleTaskFailure = (taskID, result) => {
    updateLoading(result);
    stopPolling();
    removeTaskFromSessionStorage(taskID);
    console.error(`Task ${taskID} failed`);
    console.error(`${result}`);
  };

  /**
   * Handle task revoked
   * stop polling
   * stop loading
   * remove task from session storage
   */
  const handleTaskRevoked = (taskID) => {
    updateLoading();
    stopPolling();
    removeTaskFromSessionStorage(taskID);
    console.error(`Task ${taskID} was revoked`);
  };
  
  /**
   * Reset the poller state
   */
  const reset = () => {
    stopPolling();
    taskStatus.value = null;
    isLoading.value = false;
    error.value = null;
    result.value = null;
  };
  
  /**
   * Check if polling is currently active
   */
  const isActive = () => isPolling.value;
  
  /**
   * Get current task status
   */
  const getStatus = () => taskStatus.value;
  
  /**
   * Get current result
   */
  const getResult = () => result.value;
  
  /**
   * Get current error
   */
  const getError = () => error.value;
  
  /**
   * Check if task is complete (success or failure)
   */
  const isComplete = () => {
    return taskStatus.value === 'SUCCESS' || taskStatus.value === 'FAILURE' || taskStatus.value === 'REVOKED';
  };
  
  /**
   * Check if task is in progress
   */
  const isInProgress = () => {
    return taskStatus.value === 'PENDING' || taskStatus.value === 'STARTED' || taskStatus.value === 'RETRY';
  };
  
  return {
    // Reactive state
    taskStatus,
    isLoading,
    error,
    result,
    
    // Control methods
    startPolling,
    stopPolling,
    checkTaskStatus,
    reset,
    
    // Utility methods
    isActive,
    getStatus,
    getResult,
    getError,
    isComplete,
    isInProgress,
    
    // Legacy compatibility (deprecated - use createCeleryTaskPoller instead)
    pollingInterval: null,
  };
}

// Export the updateLoading function
export { updateLoading };
